'use client';

import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';

import { useGlobalUI } from 'contexts/GlobalUIContext';
import AccuracyStudiesSection from 'components/science/accuracy/AccuracyStudiesSection';
import HeroSection from 'components/science/landing-v2/HeroSection';
import ResearchStudiesSection from 'components/science/landing-v2/ResearchStudiesSection';
import AskOurDataSection from 'components/science/ask/AskOurDataSection';
import type { AskQuestion } from 'components/science/ask/askData';
import type { QueryBlock } from 'components/science/landing-v2/search/conversation';
import type { AccuracyArea } from 'components/science/accuracy/accuracyLedger';
import type { Study } from 'content/science/studyTypes';
import type { CmsAccuracyStudy, CmsQuestion } from 'content/science/cms';

import styles from './studies-client.module.css';

/* Loaded on first open. The panel renders nothing until then, and its engine
   and corpus builder were a third of this route's JS. Declared here rather
   than on the server page: a Server Component may not call dynamic() with
   ssr: false. */
const SearchPanel = dynamic(
  () => import('components/science/landing-v2/search/SearchPanel'),
  { ssr: false },
);

const SEARCH_PANEL_ID = 'science-search-panel';

interface StudiesListingProps {
  studies: Study[];
  /* The accuracy ledger's areas, passed down from the server page so the
     368KB plot JSON behind the ledger never enters a client graph. */
  areas: AccuracyArea[];
  extraAccuracy?: CmsAccuracyStudy[];
  extraQuestions?: CmsQuestion[];
  /* The rail's list, CMS merged over the vendored extract by the route. */
  askQuestions?: AskQuestion[];
}

export default function StudiesListing({
  studies,
  areas,
  extraAccuracy,
  extraQuestions,
  askQuestions,
}: StudiesListingProps) {
  useGlobalUI({ headerCollaspable: false });

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchMounted, setSearchMounted] = useState(false);
  const [blocks, setBlocks] = useState<QueryBlock[]>([]);
  const openSearch = () => {
    setSearchMounted(true);
    setSearchOpen(true);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchMounted(true);
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const { research, allSorted } = useMemo(() => {
    const sortByDate = (a: Study, b: Study) =>
      (b.publishedAt || '').localeCompare(a.publishedAt || '');

    /* Research = everything that is not a byte. White papers, peer-reviewed
       publications, medRxiv pre-prints and conference posters all share the
       same card layout. */
    const researchKinds: Study['kind'][] = [
      'white-paper',
      'peer-reviewed',
      'medrxiv',
      'conference-poster',
    ];
    const researchArr = studies
      .filter((s) => researchKinds.includes(s.kind))
      .sort(sortByDate);

    /* Search index = every kind, newest-first. */
    return { research: researchArr, allSorted: [...studies].sort(sortByDate) };
  }, [studies]);

  return (
    <>
      <main className={styles.page}>
        <HeroSection
          onOpenSearch={openSearch}
          searchOpen={searchOpen}
          searchPanelId={SEARCH_PANEL_ID}
        />
        <AccuracyStudiesSection areas={areas} />
        <ResearchStudiesSection studies={research} />
        {/* No explainers band here: landing-v2/ExplainersEntrySection is not
            ported — one of its three hardcoded rows (slug 'sleep-score') is not
            an explainers slug, so it would ship a 404. Re-derive from
            `git show science-studies-page-v3:components/science/landing-v2/ExplainersEntrySection.tsx`
            if the hub ever wants it. */}
        <AskOurDataSection questions={askQuestions} />
      </main>
      {searchMounted ? (
        <SearchPanel
          id={SEARCH_PANEL_ID}
          open={searchOpen}
          studies={allSorted}
          areas={areas}
          extraAccuracy={extraAccuracy}
          extraQuestions={extraQuestions}
          blocks={blocks}
          onBlocks={setBlocks}
          onClose={() => setSearchOpen(false)}
        />
      ) : null}
    </>
  );
}
